import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import Loading from "../beta/components/helpers/Loading";
import {
    getApiErrorMessage,
    request,
    requestWithHeaders,
} from "../beta/utils/fetchData";
import { checkForXSS, safeRedirect } from "../beta/utils/queryStringHelper";

import LmLoader from "./Lm-Loader";

const isValidEmail = (value) => {
    if (!value) {
        return false;
    }

    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
};

const LoginForm = ({ onSubmit }) => {
    const [loginSubmitted, setLoginSubmitted] = useState(false);
    const [ssoSubmitted, setSsoSubmitted] = useState(false);
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [ssoUsername, setSSOUsername] = useState("");
    const [loginDirty, setLoginDirty] = useState(false);
    const [ssoDirty, setSsoDirty] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [loginErrors, setLoginErrors] = useState([]);
    const [ssoErrors, setSsoErrors] = useState([]);
    const [loginRequestInProgress, setLoginRequestInProgress] = useState(false);
    const [ssoRequestInProgress, setSsoRequestInProgress] = useState(false);
    const [antiforgeryToken, setAntiforgeryToken] = useState("");
    const [isCheckingAuth, setIsCheckingAuth] = useState(false);
    const [ssoExpanded, setSsoExpanded] = useState(false);
    const usernameInputRef = useRef(null);
    const ssoUsernameInputRef = useRef(null);

    useEffect(() => {
        const currentUrl = new URL(window.location.href);
        const oidcError = currentUrl.searchParams.get("error");
        if (oidcError) {
            setSsoExpanded(true);
            setSsoErrors([checkForXSS(oidcError)]);

            currentUrl.searchParams.delete("error");
            window.history.replaceState(
                {},
                document.title,
                `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`
            );
        }

        setIsCheckingAuth(true);
        localStorage.removeItem("bearer-token");

        const tokenInput = document.querySelector(
            'input[name="__RequestVerificationToken"]'
        );
        const requestVerificationToken = tokenInput ? tokenInput.value : "";
        setAntiforgeryToken(requestVerificationToken);

        fetch("/api/authentication/refresh-token", {
            method: "POST",
            credentials: "include",
        })
            .then((response) => response.json())
            .then((data) => {
                // setIsAuthenticated(true);
                if (data?.accessToken) {
                    const redirectUrl = checkForXSS(
                        new URLSearchParams(window.location.search).get(
                            "ReturnUrl"
                        ) || "/"
                    );
                    safeRedirect(redirectUrl);
                } else {
                    setIsCheckingAuth(false);
                }
            })
            .catch(() => {
                setIsCheckingAuth(false);
            });
        // .finally(() => setIsCheckingAuth(false));
    }, []);

    useEffect(() => {
        if (isCheckingAuth) {
            return undefined;
        }

        const activeInput = ssoExpanded
            ? ssoUsernameInputRef.current
            : usernameInputRef.current;

        const focusTimer = window.setTimeout(() => {
            if (activeInput) {
                activeInput.focus();
            }
        }, 250);

        return () => window.clearTimeout(focusTimer);
    }, [isCheckingAuth, ssoExpanded]);

    const handleLoginSubmit = (e) => {
        e.preventDefault();
        setLoginSubmitted(true);

        if (!username || !password) {
            return;
        }

        if (typeof onSubmit === "function") {
            onSubmit(
                { username, password, rememberMe, antiforgeryToken },
                {
                    setErrors: setLoginErrors,
                    setRequestInProgress: setLoginRequestInProgress,
                    setSubmitted: setLoginSubmitted,
                }
            );
        }
    };

    const handleOktaLogin = (e) => {
        e.preventDefault();
        setSsoSubmitted(true);
        setSsoErrors([]);

        if (!ssoUsername) {
            return;
        }

        // if (!isValidEmail(ssoUsername)) {
        //     return;
        // }

        const loginWithOkta = async () => {
            setSsoRequestInProgress(true);

            try {
                const result = await requestWithHeaders(
                    "/api/authentication/okta-login",
                    "POST",
                    {
                        ssoUsername: ssoUsername,
                        returnUrl:
                            new URLSearchParams(window.location.search).get(
                                "ReturnUrl"
                            ) || "/",
                    },
                    {
                        "X-XSRF-TOKEN": antiforgeryToken,
                    }
                );

                if (result.response && result.response.ok) {
                    const data = (result && result.data) || {};
                    const redirectUrl = checkForXSS(data.redirectUrl || "");
                    if (redirectUrl) {
                        safeRedirect(redirectUrl);
                        return;
                    }
                }

                setSsoErrors([
                    getApiErrorMessage(result, "Failed to initiate SSO login."),
                ]);
            } catch {
                setSsoErrors(["Failed to initiate SSO login."]);
            } finally {
                setSsoRequestInProgress(false);
            }
        };

        loginWithOkta();
    };

    const handleSsoAccordionChange = (_, expanded) => {
        setSsoExpanded(expanded);

        if (!expanded) {
            setSsoSubmitted(false);
            setSsoErrors([]);
        }
    };

    // Show loading while checking authentication status
    if (isCheckingAuth) {
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                }}
            >
                <Loading enabled={true} spinnerText="Loading..." />
            </div>
        );
    }

    return (
        <div className="login-page-wrapper">
            <div id="login-block" className="login-block-container">
                <div className="login-block-header">
                    <img
                        className="logo"
                        src="/images/ISSLiquidMetrixLogo.png"
                        alt="LiquidMetrix Logo"
                    />
                    <div className="errors">
                        {loginErrors.length > 0 &&
                            loginErrors.map((err, i) => <p key={i}>{err}</p>)}
                    </div>
                </div>

                <div className="login-block-body">
                    <form
                        method="post"
                        autoComplete="off"
                        noValidate
                        onSubmit={handleLoginSubmit}
                    >
                        <div className="lm-input-group">
                            <input
                                ref={usernameInputRef}
                                type="text"
                                name="username"
                                spellCheck={false}
                                autoComplete="new-username"
                                required
                                value={username}
                                onChange={(e) => {
                                    setUsername(e.target.value);
                                    setLoginDirty(true);
                                }}
                                disabled={loginRequestInProgress}
                                className={
                                    !username && (loginDirty || loginSubmitted)
                                        ? "has-error"
                                        : ""
                                }
                            />
                            <span className="highlight"></span>
                            <span className="bar"></span>
                            <label htmlFor="username">Username</label>
                            <span className="error">
                                {!username &&
                                    (loginDirty || loginSubmitted) &&
                                    "The username field is required."}
                            </span>
                        </div>

                        <div
                            className="lm-input-group"
                            style={{ marginBottom: 0 }}
                        >
                            <input
                                type="password"
                                name="password"
                                autoComplete="new-password"
                                required
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    setLoginDirty(true);
                                }}
                                disabled={loginRequestInProgress}
                                className={
                                    !password && (loginDirty || loginSubmitted)
                                        ? "has-error"
                                        : ""
                                }
                            />
                            <span className="highlight"></span>
                            <span className="bar"></span>
                            <label htmlFor="password">Password</label>
                            <span className="error">
                                {!password &&
                                    (loginDirty || loginSubmitted) &&
                                    "The password field is required."}
                            </span>
                        </div>

                        <div className="actions">
                            <div className="lm-checkbox lm-checkbox-inline push-left">
                                <input
                                    type="checkbox"
                                    id="remember-me"
                                    checked={rememberMe}
                                    onChange={(e) =>
                                        setRememberMe(e.target.checked)
                                    }
                                    disabled={loginRequestInProgress}
                                />
                                <label htmlFor="remember-me">Remember Me</label>
                            </div>
                            <div className="push-right">
                                <Link
                                    to="/Account/ForgotPassword"
                                    className="lm-link"
                                >
                                    <span>Forgot Password?</span>
                                </Link>
                            </div>
                        </div>

                        <button
                            className={`lm-btn primary sign-in-btn${
                                loginRequestInProgress ? " loading" : ""
                            }`}
                            type="submit"
                            disabled={
                                loginRequestInProgress || ssoRequestInProgress
                            }
                        >
                            <span className="btn-label">SIGN IN</span>
                            <LmLoader />
                        </button>
                    </form>
                </div>

                <div className="login-block-divider">
                    <hr className="login-block-divider-line" />
                    <span className="login-block-divider-label">OR</span>
                    <hr className="login-block-divider-line" />
                </div>

                <div className="login-sso-shutter-block">
                    <div className="login-sso-shutter-title">
                        Sign in with SSO
                    </div>

                    <Accordion
                        className="login-sso-shutter"
                        expanded={ssoExpanded}
                        onChange={handleSsoAccordionChange}
                        disableGutters
                        elevation={0}
                    >
                        <AccordionSummary
                            className="login-sso-shutter-summary"
                            expandIcon={
                                <ExpandMoreIcon className="login-sso-expand-icon" />
                            }
                        >
                            <span
                                className="login-sso-shutter-summary-placeholder"
                                aria-hidden="true"
                            />
                        </AccordionSummary>
                        <AccordionDetails className="login-sso-shutter-details">
                            <div className="login-block-body">
                                {ssoErrors.length > 0 && (
                                    <div className="errors login-sso-errors">
                                        {ssoErrors.map((err, i) => (
                                            <p key={i}>{err}</p>
                                        ))}
                                    </div>
                                )}
                                <form
                                    method="post"
                                    autoComplete="off"
                                    noValidate
                                    onSubmit={handleOktaLogin}
                                >
                                    <div className="lm-input-group">
                                        <input
                                            ref={ssoUsernameInputRef}
                                            type="email"
                                            name="email"
                                            spellCheck={false}
                                            autoComplete="new-email"
                                            required
                                            value={ssoUsername}
                                            onChange={(e) => {
                                                setSSOUsername(e.target.value);
                                                setSsoDirty(true);
                                            }}
                                            disabled={ssoRequestInProgress}
                                            className={
                                                (ssoDirty || ssoSubmitted) &&
                                                !ssoUsername
                                                    ? "has-error"
                                                    : ""
                                            }
                                        />
                                        <span className="highlight"></span>
                                        <span className="bar"></span>
                                        <label htmlFor="email">
                                            SSO Username
                                        </label>
                                        <span className="error">
                                            {(ssoDirty || ssoSubmitted) &&
                                                !ssoUsername &&
                                                "The SSO Username field is required."}
                                        </span>
                                    </div>

                                    <button
                                        className={`lm-btn primary sign-in-btn${
                                            ssoRequestInProgress
                                                ? " loading"
                                                : ""
                                        }`}
                                        type="submit"
                                        style={{
                                            marginTop: "30px",
                                        }}
                                        disabled={
                                            ssoRequestInProgress ||
                                            loginRequestInProgress
                                        }
                                    >
                                        <span className="btn-label">
                                            SIGN IN
                                        </span>
                                        <LmLoader />
                                    </button>
                                </form>
                            </div>
                        </AccordionDetails>
                    </Accordion>
                </div>
            </div>
        </div>
    );
};

export default LoginForm;
